import { FelaCSS, FelaCSSWithCustomSelectors, colors } from '@bridebook/ui';
import { ArticleTileSizes } from '@bridebook/web/app-shared/lib/articles/types';

interface IStyles {
  wrapper: FelaCSS;
  articleTile: FelaCSS;
  articleTileImage: FelaCSSWithCustomSelectors;
  articleTileImageWrap: FelaCSSWithCustomSelectors;
  articleLink: FelaCSS;
  titleCategoryWrap: FelaCSS;
  category: FelaCSS;
  title: FelaCSS;
  imageWrap: FelaCSS;
  featuredPill: FelaCSS;
  bookmarkWrapper: FelaCSS;
}

const styles = (
  imageUrl: string,
  size?: ArticleTileSizes,
  firstArticle?: boolean,
  index?: number,
): IStyles => {
  const isRelatedArticles = typeof index === 'number';
  const smallSize = size === 'small';

  const titleShared: FelaCSS = {
    display: 'inline',
    color: colors.space,
    textAlign: 'left',
    margin: 0,
  };

  const titleSmall: FelaCSS = {
    ...titleShared,
    fontDefaultSemiBold: 14,
    lineHeight: '19px',
    textAlign: 'left',
    color: colors.space75,
  };

  const titleCategorySmall: FelaCSS = {
    position: 'relative',
    textAlign: 'left',
    marginLeft: '10px',
  };

  return {
    wrapper: {
      flexGrow: 1,
      position: 'relative',
      borderRadius: '16px',
      overflow: 'hidden',
      backgroundColor: colors.white,
      boxSizing: 'border-box',
      ...(smallSize && {
        borderRadius: 0,
        paddingBottom: 4,
        borderBottom: `1px solid ${colors.space08}`,
      }),
      ...(!firstArticle && !isRelatedArticles && { minHeight: 280 }),
      ...(firstArticle && {
        maxHeight: 600,
        maxWidthTabletSm: {
          marginBottom: 6,
        },
      }),
      ...(isRelatedArticles && {
        marginVertical: 2,
      }),
    },
    articleTile: smallSize
      ? {
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          borderRadius: 16,
        }
      : {
          position: 'relative',
          ...(isRelatedArticles && { height: 280 }),
        },
    articleTileImage: {
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      borderRadius: smallSize ? '8px' : '16px',
      ...(!isRelatedArticles && {
        height: 220,
        zIndex: 200,
        overflow: 'hidden',
        transition: 'transform .5s ease',
        borderRadius: smallSize ? '8px' : '16px',
        ':hover': {
          transform: 'scale(1.2)',
          overflow: 'hidden',
        },
      }),
      ...(isRelatedArticles &&
        !smallSize && {
          height: 450,
        }),
      ...(firstArticle && {
        width: '100%',
        maxWidth: 1050,
        height: 300,
        maxWidthTabletSm: {
          height: 420,
        },
        overflow: 'hidden',
      }),
      ...(isRelatedArticles &&
        smallSize && {
          width: 90,
          height: 65,
        }),
    },

    titleCategoryWrap: smallSize
      ? titleCategorySmall
      : {
          position: 'relative',
          backgroundColor: colors.white,
          paddingVertical: 4,
          zIndex: 300,
          ...(firstArticle && {
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            padding: 8,
            border: '1px solid',
            borderColor: colors.space15,
            boxShadow: '0 5px 15px 0 rgba(0, 0, 0, 0.05)',
            borderRadius: '16px',
            maxWidth: 387,

            maxWidthTabletSm: {
              bottom: 0,
              top: 'initial',
              transform: 'initial',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              color: colors.white,
              padding: 4,
              maxWidth: '100%',
              margin: 4,
            },
          }),
          ...(isRelatedArticles && { padding: 0, marginVertical: 2 }),
        },
    category: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
      color: colors.space60,
      fontDefault: 12,
      textAlign: 'left',
    },
    title: smallSize
      ? titleSmall
      : {
          ...titleShared,
          textAlign: 'left',
          fontDefaultSemiBold: firstArticle ? 24 : 18,
          lineHeight: '28px',
          letterSpacing: '0px',
          ...(firstArticle && {
            fontDefaultSemiBold: 24,

            maxWidthTabletSm: {
              paddingVertical: 2,
              color: colors.white,
            },
          }),
          ...(isRelatedArticles && {
            paddingTop: 2,
          }),
        },
    imageWrap: smallSize
      ? {
          position: 'relative',
          minHeight: '65px',
          maxHeight: '65px',
          overflow: 'visible',
          borderRadius: '8px',
        }
      : {
          borderRadius: '16px',
          position: 'relative',
          ...(!isRelatedArticles && {
            height: 220,
          }),
          ...(firstArticle && {
            alignItems: 'flex-end',
            height: 300,
            maxWidthTabletSm: {
              height: 420,
            },
          }),
        },
    featuredPill: {
      position: 'absolute',
      top: 0,
      left: 0,
      margin: 4,
      backgroundColor: colors.white,
      color: colors.space,
      paddingVertical: 2,
      paddingLeft: 4,
      paddingRight: 4,
      borderRadius: 16,
      fontDefault: 12,
    },
    bookmarkWrapper: {
      position: 'absolute',
      borderRadius: '50%',
      backgroundColor: colors.white,
      width: '30px',
      height: '30px',
      margin: 4,
      top: 0,
      right: 0,
      zIndex: 999,
    },
    articleLink: {
      color: colors.space,
      textDecoration: 'none',

      ':hover': {
        color: colors.space,
      },

      ':active': {
        color: colors.space,
      },

      ':focus': {
        color: colors.space,
      },
    },
    articleTileImageWrap: {
      overflow: 'hidden',
      borderRadius: smallSize ? '8px' : '16px',
      ...(firstArticle && {
        width: 820,
      }),
    },
  };
};

export default styles;
